import { onBeforeUnmount, onMounted, type ShallowRef, shallowRef } from 'vue';
import { currentLocale } from '@/app/i18n';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import { restApi } from '@/services/api';
import { session } from '@/services/Session';
import { kioskJourney } from '@/services/telemetry/kiosk-journey';
import { sentryService } from '@/services/telemetry/sentry';
import {
  errorMessage,
  warnTelemetryFailure,
} from '@/shared/utils/track-telemetry';

const ALLOWED_NEXT_STEPS = [
  'photo-from-webcam',
  'photo-from-phone',
  'art-style',
] as const;

type GdprNextStep = (typeof ALLOWED_NEXT_STEPS)[number];

const DEFAULT_NEXT_STEP: GdprNextStep = 'photo-from-webcam';

interface UseGdprConsentFlowResult {
  actionTaken: ShallowRef<boolean>;
  onConsent: () => void;
  onDecline: () => Promise<void>;
  onPrivacyPolicyClick: () => void;
}

interface UseGdprConsentFlowOptions {
  pulseConsentButton: () => void;
}

function isGdprNextStep(value: unknown): value is GdprNextStep {
  return (
    typeof value === 'string' &&
    ALLOWED_NEXT_STEPS.some((step) => step === value)
  );
}

export function useGdprConsentFlow({
  pulseConsentButton,
}: UseGdprConsentFlowOptions): UseGdprConsentFlowResult {
  const ui = useUiStore();
  const actionTaken = shallowRef(false);
  let isUnmounted = false;

  function reportInitializationError(error: unknown): void {
    // biome-ignore lint/suspicious/noConsole: preserve operational diagnostics
    console.error('[GDPRConsent] Error in mounted:', error);
    sentryService.captureException(error, {
      tags: { component: 'GDPRConsent', lifecycle: 'mounted' },
      extra: { action: 'screen_initialization' },
    });
  }

  function onConsent(): void {
    if (actionTaken.value) {
      return;
    }
    actionTaken.value = true;

    try {
      const consentTimestamp = new Date().toISOString();
      kioskJourney.record('consent_result', { consent_status: 'accepted' });

      sentryService.addBreadcrumb(
        'gdpr',
        'User accepted GDPR consent',
        {
          action: 'consent_accepted',
          timestamp: consentTimestamp,
        },
        'info'
      );
      sentryService.setTag('gdpr_consent', 'accepted');
      sentryService.incrementCounter('gdpr.consent', 1, {
        action: 'accepted',
      });

      void session.setGdprConsent(true, consentTimestamp);

      // The append-only audit is best-effort and must not delay navigation.
      if (session.id) {
        void restApi
          .recordPrivacyAcceptance(session.id, {
            consent_at: consentTimestamp,
            locale: store.currentLocale?.code || null,
            policy_profile_version_id:
              store.kiosk?.policy_profile_version_id ?? null,
          })
          .catch((error: unknown) =>
            warnTelemetryFailure(
              '[GDPRConsent] privacy acceptance audit failed:',
              error
            )
          );
      }

      const requestedNext = router.query?.next;
      const nextRoute = isGdprNextStep(requestedNext)
        ? requestedNext
        : DEFAULT_NEXT_STEP;

      if (requestedNext && nextRoute !== requestedNext) {
        // biome-ignore lint/suspicious/noConsole: preserve security diagnostics
        console.warn(
          `[GDPRConsent] Rejected invalid ?next="${requestedNext}", using "${nextRoute}"`
        );
        sentryService.addBreadcrumb(
          'security',
          'Rejected invalid next step in GDPR consent',
          {
            requested: String(requestedNext).slice(0, 100),
            resolved: nextRoute,
          },
          'warning'
        );
      }

      sentryService.addBreadcrumb(
        'navigation',
        `Navigating after consent to: ${nextRoute}`,
        { nextRoute },
        'info'
      );

      void router.redirectToStep(nextRoute);
    } catch (error) {
      actionTaken.value = false;
      // biome-ignore lint/suspicious/noConsole: preserve operational diagnostics
      console.error('[GDPRConsent] Error in onConsent:', error);
      sentryService.captureException(error, {
        tags: { component: 'GDPRConsent', action: 'consent' },
        extra: {
          action: 'consent_acceptance',
          nextRoute: router.query?.next || DEFAULT_NEXT_STEP,
        },
      });
    }
  }

  async function onDecline(): Promise<void> {
    if (actionTaken.value || isUnmounted) {
      return;
    }
    actionTaken.value = true;
    const loadingIndicator = ui.createLoadingIndicator();

    try {
      const declineTimestamp = new Date().toISOString();
      kioskJourney.record('consent_result', { consent_status: 'declined' });
      kioskJourney.end('gdpr_declined');

      sentryService.addBreadcrumb(
        'gdpr',
        'User declined GDPR consent',
        {
          action: 'consent_declined',
          timestamp: declineTimestamp,
        },
        'warning'
      );
      sentryService.setTag('gdpr_consent', 'declined');
      sentryService.incrementCounter('gdpr.consent', 1, {
        action: 'declined',
      });

      sentryService.addBreadcrumb(
        'navigation',
        'Navigating to home after consent decline',
        { reason: 'gdpr_declined' },
        'info'
      );

      // Ending the session clears consent without an async consent=false update.
      try {
        await session.end('abandoned');
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: preserve operational reset diagnostics.
        console.warn('[GDPRConsent] Session end failed:', errorMessage(error));
      }

      if (!isUnmounted) {
        await router.redirectToStep('', {
          omitQuery: true,
          localeCode: currentLocale.value?.code,
        });
      }
    } catch (error) {
      actionTaken.value = false;
      // biome-ignore lint/suspicious/noConsole: preserve operational diagnostics
      console.error('[GDPRConsent] Error in onDecline:', error);
      sentryService.captureException(error, {
        tags: { component: 'GDPRConsent', action: 'decline' },
        extra: { action: 'consent_decline' },
      });
    } finally {
      loadingIndicator.hide();
    }
  }

  function onPrivacyPolicyClick(): void {
    try {
      sentryService.addBreadcrumb(
        'gdpr',
        'User opened privacy policy',
        {
          action: 'privacy_policy_viewed',
          timestamp: new Date().toISOString(),
        },
        'info'
      );
      sentryService.incrementCounter('gdpr.privacy_policy_views', 1);
      ui.showPrivacyPolicyPopup = true;
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: preserve operational diagnostics
      console.error('[GDPRConsent] Error in onPrivacyPolicyClick:', error);
      sentryService.captureException(error, {
        tags: { component: 'GDPRConsent', action: 'privacy_policy' },
        extra: { action: 'privacy_policy_click' },
      });
    }
  }

  onMounted(() => {
    try {
      if (!session.id) {
        // biome-ignore lint/suspicious/noConsole: preserve operational diagnostics
        console.warn('[GDPRConsent] Mounted without active session');
        sentryService.addBreadcrumb(
          'gdpr',
          'GDPR consent screen mounted without active session',
          { hasSession: false },
          'warning'
        );
      }

      sentryService.addBreadcrumb(
        'gdpr',
        'GDPR consent screen displayed',
        {
          step: 'gdpr-consent',
          timestamp: new Date().toISOString(),
          sessionId: session.id,
          requestedNext: router.query?.next || null,
        },
        'info'
      );

      pulseConsentButton();
    } catch (error) {
      reportInitializationError(error);
    }
  });

  onBeforeUnmount(() => {
    isUnmounted = true;
    if (ui.showPrivacyPolicyPopup) {
      ui.showPrivacyPolicyPopup = false;
    }
  });

  return {
    actionTaken,
    onConsent,
    onDecline,
    onPrivacyPolicyClick,
  };
}
